import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { apiBaseUrl } from '../../config/runtime';
import { formatDuration } from '../../utils/duration';
import './KanbanBoard.css';

const emptyForm = {
  title: '',
  description: '',
  type: 'cor',
  demand_kind: 'corretiva',
  priority: 'media',
  impact: 'medio',
  urgency: 'media',
  status: 'aberto',
  owner: '',
  customer_id: '',
  company_id: '',
  workspace_id: '',
  start_date: '',
  due_date: '',
  sla_due: '',
};

const oneDay = 24 * 60 * 60 * 1000;
const projectSlaPolicy = {
  corretiva: { critica: 4, alta: 8, media: 24, baixa: 48 },
  evolutiva: { critica: 24, alta: 72, media: 168, baixa: 336 },
};
const projectSloPolicy = {
  corretiva: { critica: 95, alta: 93, media: 90, baixa: 88 },
  evolutiva: { critica: 92, alta: 90, media: 88, baixa: 85 },
};
const priorityWeight = { critica: 4, alta: 3, media: 2, baixa: 1 };
const PROJECT_TYPE_OPTIONS = [
  { value: 'cor', label: 'COR - Correções de SW' },
  { value: 'sdm', label: 'SDM - Sugestões de Melhoria' },
  { value: 'dev', label: 'DEV - Desenvolvimento' },
  { value: 'rma', label: 'RMA - Componentes' },
  { value: 'issue_correcao', label: 'Issue Correção (legado)' },
  { value: 'incidente', label: 'Incidente (legado)' },
  { value: 'problema', label: 'Problema (legado)' },
  { value: 'mudanca', label: 'Mudança (legado)' },
  { value: 'demanda', label: 'Demanda (legado)' },
  { value: 'melhoria', label: 'Melhoria (legado)' },
];

const inferDemandKind = (type) => {
  const normalized = (type || '').toLowerCase();
  if (['cor', 'correcao_sw', 'incidente', 'problema', 'corretiva', 'issue_correcao', 'bug', 'correcao', 'rma'].includes(normalized)) {
    return 'corretiva';
  }
  return 'evolutiva';
};

const toDate = (value) => {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

const formatDate = (value) => {
  const date = toDate(value);
  return date ? date.toLocaleDateString() : '-';
};

const startOfDay = (date) => new Date(date.getFullYear(), date.getMonth(), date.getDate());
const endOfDay = (date) => new Date(date.getFullYear(), date.getMonth(), date.getDate(), 23, 59, 59, 999);

const formatDemandKindLabel = (value) => {
  if ((value || '').toLowerCase() === 'evolutiva') return 'Evolutiva';
  return 'Corretiva';
};

const formatStatusLabel = (value) => {
  const normalized = (value || '').toLowerCase();
  const labels = {
    aberto: 'Aberto',
    planejamento: 'Planejamento',
    em_andamento: 'Em andamento',
    em_validacao: 'Em validacao',
    concluido: 'Concluido',
    bloqueado: 'Bloqueado',
    cancelado: 'Cancelado',
  };
  return labels[normalized] || value || '-';
};

const formatPriorityLabel = (value) => {
  const normalized = (value || '').toLowerCase();
  const labels = {
    baixa: 'Baixa',
    media: 'Media',
    alta: 'Alta',
    critica: 'Critica',
  };
  return labels[normalized] || value || '-';
};

const formatProjectTypeLabel = (value) => {
  const normalized = (value || '').toLowerCase();
  const labels = {
    cor: 'COR',
    sdm: 'SDM',
    dev: 'DEV',
    rma: 'RMA',
    issue_correcao: 'Issue Correcao',
    incidente: 'Incidente',
    problema: 'Problema',
    mudanca: 'Mudanca',
    demanda: 'Demanda',
    melhoria: 'Melhoria',
  };
  return labels[normalized] || value || '-';
};

const getProjectPolicy = (demandKind, priority) => {
  const effectiveDemandKind = (demandKind || 'corretiva').toLowerCase() === 'evolutiva' ? 'evolutiva' : 'corretiva';
  const normalizedPriority = (priority || '').toLowerCase();
  const effectivePriority = ['baixa', 'media', 'alta', 'critica'].includes(normalizedPriority) ? normalizedPriority : 'media';
  return {
    slaHours: projectSlaPolicy[effectiveDemandKind][effectivePriority],
    sloTarget: projectSloPolicy[effectiveDemandKind][effectivePriority],
  };
};

const reorderListById = (items, draggedId, targetId) => {
  const sourceIndex = items.findIndex((item) => Number(item.id) === Number(draggedId));
  const targetIndex = items.findIndex((item) => Number(item.id) === Number(targetId));
  if (sourceIndex < 0 || targetIndex < 0 || sourceIndex === targetIndex) {
    return items;
  }
  const reordered = [...items];
  const [moved] = reordered.splice(sourceIndex, 1);
  reordered.splice(targetIndex, 0, moved);
  return reordered.map((item, index) => ({ ...item, position: index + 1 }));
};

const KanbanBoard = () => {
  const [stages, setStages] = useState([]);
  const [projects, setProjects] = useState([]);
  const [workspaces, setWorkspaces] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [viewMode, setViewMode] = useState('kanban');
  const [selectedWorkspaceId, setSelectedWorkspaceId] = useState('all');
  const [newStage, setNewStage] = useState({ name: '', color: '#B10905', position: '' });
  const [editingStage, setEditingStage] = useState(null);
  const [showStageManager, setShowStageManager] = useState(false);
  const [showWorkspaceManager, setShowWorkspaceManager] = useState(false);
  const [newWorkspace, setNewWorkspace] = useState({ name: '', color: '#65202A' });
  const [editingWorkspace, setEditingWorkspace] = useState(null);
  const [dragWorkspaceId, setDragWorkspaceId] = useState(null);
  const [dragOverWorkspaceId, setDragOverWorkspaceId] = useState(null);
  const [dragStageConfigId, setDragStageConfigId] = useState(null);
  const [dragOverStageConfigId, setDragOverStageConfigId] = useState(null);
  const [draggingId, setDraggingId] = useState(null);
  const [dragOverStage, setDragOverStage] = useState(null);
  const [selectedProject, setSelectedProject] = useState(null);
  const [projectModalOpen, setProjectModalOpen] = useState(false);
  const [projectForm, setProjectForm] = useState(emptyForm);
  const [demandKindFilter, setDemandKindFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [matrixQuery, setMatrixQuery] = useState('');
  const [matrixPriorityFilter, setMatrixPriorityFilter] = useState('all');
  const [matrixSlaFilter, setMatrixSlaFilter] = useState('all');
  const [matrixSortField, setMatrixSortField] = useState('priority');
  const [matrixSortDirection, setMatrixSortDirection] = useState('desc');
  const orderedStages = useMemo(
    () => [...stages].sort((a, b) => (a.position ?? 0) - (b.position ?? 0) || ((a.id ?? 0) - (b.id ?? 0))),
    [stages]
  );
  const orderedWorkspaces = useMemo(
    () => [...workspaces].sort((a, b) => (a.position ?? 0) - (b.position ?? 0) || ((a.id ?? 0) - (b.id ?? 0))),
    [workspaces]
  );
  const formPolicyPreview = useMemo(() => {
    const inferredDemandKind = projectForm.demand_kind || inferDemandKind(projectForm.type);
    const policy = getProjectPolicy(inferredDemandKind, projectForm.priority);
    return {
      demandKind: inferredDemandKind,
      slaHours: policy.slaHours,
      sloTarget: policy.sloTarget,
    };
  }, [projectForm.demand_kind, projectForm.priority, projectForm.type]);

  const loadData = useCallback(async () => {
    try {
      const [stageRes, projectRes, workspaceRes, customerRes, companyRes] = await Promise.all([
        axios.get(`${apiBaseUrl}/projects/stages`),
        axios.get(`${apiBaseUrl}/projects`),
        axios.get(`${apiBaseUrl}/projects/workspaces`),
        axios.get(`${apiBaseUrl}/customers`),
        axios.get(`${apiBaseUrl}/companies`),
      ]);
      const stageList = stageRes.data || [];
      const workspaceList = workspaceRes.data || [];
      setStages(stageList);
      setProjects(projectRes.data || []);
      setWorkspaces(workspaceList);
      setCustomers(customerRes.data || []);
      setCompanies(companyRes.data || []);
      setProjectForm((prev) => {
        if (prev.workspace_id) return prev;
        const defaultWorkspaceId = workspaceList[0]?.id;
        if (!defaultWorkspaceId) return prev;
        return { ...prev, workspace_id: defaultWorkspaceId };
      });
    } catch (error) {}
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleStageSave = async () => {
    if (!newStage.name.trim()) return;
    const payload = {
      name: newStage.name.trim(),
      color: newStage.color,
      position: newStage.position ? Number(newStage.position) : undefined,
    };
    try {
      if (editingStage) {
        await axios.put(`${apiBaseUrl}/projects/stages/${editingStage}`, payload);
      } else {
        await axios.post(`${apiBaseUrl}/projects/stages`, {
          ...payload,
          position: payload.position ?? stages.length + 1,
        });
      }
      setNewStage({ name: '', color: '#B10905', position: '' });
      setEditingStage(null);
      loadData();
    } catch (error) {}
  };

  const handleStageDelete = async (stageId) => {
    try {
      await axios.delete(`${apiBaseUrl}/projects/stages/${stageId}`);
      loadData();
    } catch (error) {}
  };

  const handleStageEdit = (stage) => {
    setEditingStage(stage.id);
    setNewStage({ name: stage.name, color: stage.color || '#B10905', position: stage.position ?? '' });
    setShowStageManager(true);
  };

  const handleWorkspaceSave = async () => {
    if (!newWorkspace.name.trim()) return;
    const payload = { name: newWorkspace.name.trim(), color: newWorkspace.color };
    try {
      if (editingWorkspace) {
        await axios.put(`${apiBaseUrl}/projects/workspaces/${editingWorkspace}`, payload);
      } else {
        await axios.post(`${apiBaseUrl}/projects/workspaces`, payload);
      }
      setEditingWorkspace(null);
      setNewWorkspace({ name: '', color: '#65202A' });
      loadData();
    } catch (error) {}
  };

  const handleWorkspaceEdit = (workspace) => {
    setEditingWorkspace(workspace.id);
    setNewWorkspace({ name: workspace.name, color: workspace.color || '#65202A' });
    setShowWorkspaceManager(true);
  };

  const handleWorkspaceDelete = async (workspaceId) => {
    try {
      await axios.delete(`${apiBaseUrl}/projects/workspaces/${workspaceId}`);
      if (String(selectedWorkspaceId) === String(workspaceId)) {
        setSelectedWorkspaceId('all');
      }
      loadData();
    } catch (error) {}
  };

  const persistWorkspaceOrder = async (workspaceList) => {
    const orderedIds = workspaceList.map((workspace) => workspace.id);
    try {
      await axios.put(`${apiBaseUrl}/projects/workspaces/reorder`, { ordered_ids: orderedIds });
    } catch (error) {
      loadData();
    }
  };

  const handleWorkspaceReorder = (draggedId, targetId) => {
    const nextOrder = reorderListById(orderedWorkspaces, draggedId, targetId);
    if (nextOrder === orderedWorkspaces) return;
    setWorkspaces(nextOrder);
    persistWorkspaceOrder(nextOrder);
  };

  const persistStageOrder = async (stageList) => {
    const orderedIds = stageList.map((stage) => stage.id);
    try {
      await axios.put(`${apiBaseUrl}/projects/stages/reorder`, { ordered_ids: orderedIds });
    } catch (error) {
      loadData();
    }
  };

  const handleStageReorder = (draggedId, targetId) => {
    const nextOrder = reorderListById(orderedStages, draggedId, targetId);
    if (nextOrder === orderedStages) return;
    setStages(nextOrder);
    persistStageOrder(nextOrder);
  };

  const openWorkspaceManager = () => {
    setShowStageManager(false);
    setShowWorkspaceManager(true);
  };

  const closeWorkspaceManager = () => {
    setShowWorkspaceManager(false);
    setEditingWorkspace(null);
    setNewWorkspace({ name: '', color: '#65202A' });
  };

  const openNewStage = () => {
    setShowWorkspaceManager(false);
    setEditingStage(null);
    setNewStage({ name: '', color: '#B10905', position: '' });
    setShowStageManager(true);
  };

  const closeStageManager = () => {
    setShowStageManager(false);
    setEditingStage(null);
    setNewStage({ name: '', color: '#B10905', position: '' });
  };

  const getDefaultWorkspaceId = useCallback(() => {
    if (selectedWorkspaceId && selectedWorkspaceId !== 'all') {
      return String(selectedWorkspaceId);
    }
    return orderedWorkspaces[0]?.id ? String(orderedWorkspaces[0].id) : '';
  }, [orderedWorkspaces, selectedWorkspaceId]);

  const closeProjectModal = useCallback(() => {
    setProjectModalOpen(false);
    setSelectedProject(null);
    setProjectForm({ ...emptyForm, workspace_id: getDefaultWorkspaceId() });
  }, [getDefaultWorkspaceId]);

  const openNewProject = (workspaceId = null) => {
    const workspaceTarget = workspaceId ? String(workspaceId) : getDefaultWorkspaceId();
    setSelectedProject(null);
    setProjectForm({ ...emptyForm, workspace_id: workspaceTarget });
    setProjectModalOpen(true);
  };

  const createShowcaseProjects = async () => {
    try {
      const [stageRes, workspaceRes] = await Promise.all([
        axios.get(`${apiBaseUrl}/projects/stages`),
        axios.get(`${apiBaseUrl}/projects/workspaces`),
      ]);
      const orderedStages = (stageRes.data || [])
        .slice()
        .sort((a, b) => (a.position ?? 0) - (b.position ?? 0));
      const workspacesLocal = workspaceRes.data || [];
      const defaultWorkspaceId = workspacesLocal[0]?.id || null;

      const stageByName = (text) =>
        orderedStages.find((s) => (s.name || '').toLowerCase().includes(text.toLowerCase()))?.id || null;
      const defaultStage = orderedStages[0]?.id || null;
      const now = Date.now();
      const owner = localStorage.getItem('attendantName') || 'Coordenador de Suporte';
      const showcase = [
        {
          title: 'Showcase: Mapeamento de Incidentes Recorrentes',
          description: 'Inventário de incidentes e causa raiz para reduzir retrabalho do suporte.',
          type: 'cor',
          demand_kind: 'corretiva',
          priority: 'alta',
          impact: 'alto',
          urgency: 'media',
          status: 'planejamento',
          owner,
          start_date: new Date(now - 4 * oneDay).toISOString().slice(0, 10),
          due_date: new Date(now + 8 * oneDay).toISOString().slice(0, 10),
          stage_id: stageByName('Planej') || defaultStage,
          workspace_id: defaultWorkspaceId,
        },
        {
          title: 'Showcase: Implantação de Base de Conhecimento',
          description: 'Criação de artigos e runbooks para acelerar resolução no 1º atendimento.',
          type: 'sdm',
          demand_kind: 'evolutiva',
          priority: 'media',
          impact: 'medio',
          urgency: 'media',
          status: 'em_andamento',
          owner,
          start_date: new Date(now - 9 * oneDay).toISOString().slice(0, 10),
          due_date: new Date(now + 10 * oneDay).toISOString().slice(0, 10),
          stage_id: stageByName('Exec') || defaultStage,
          workspace_id: defaultWorkspaceId,
        },
        {
          title: 'Showcase: Janela de Mudança para Integrações',
          description: 'Projeto de mudança controlada com checklist de risco e plano de rollback.',
          type: 'dev',
          demand_kind: 'evolutiva',
          priority: 'alta',
          impact: 'alto',
          urgency: 'alta',
          status: 'em_validacao',
          owner,
          start_date: new Date(now - 14 * oneDay).toISOString().slice(0, 10),
          due_date: new Date(now + 3 * oneDay).toISOString().slice(0, 10),
          stage_id: stageByName('Valid') || defaultStage,
          workspace_id: defaultWorkspaceId,
        },
        {
          title: 'Showcase: Padronização de SLA por Tipo de Chamado',
          description: 'Definição de matriz impacto/urgência e fluxos operacionais ITIL.',
          type: 'sdm',
          demand_kind: 'evolutiva',
          priority: 'critica',
          impact: 'alto',
          urgency: 'alta',
          status: 'aberto',
          owner,
          start_date: new Date(now - 1 * oneDay).toISOString().slice(0, 10),
          due_date: new Date(now + 15 * oneDay).toISOString().slice(0, 10),
          stage_id: stageByName('Backlog') || defaultStage,
          workspace_id: defaultWorkspaceId,
        },
        {
          title: 'Showcase: Projeto Encerrado de Automação de Triagem',
          description: 'Projeto concluído para exibir referência de histórico no roadmap.',
          type: 'dev',
          demand_kind: 'evolutiva',
          priority: 'baixa',
          impact: 'baixo',
          urgency: 'baixa',
          status: 'concluido',
          owner,
          start_date: new Date(now - 30 * oneDay).toISOString().slice(0, 10),
          due_date: new Date(now - 7 * oneDay).toISOString().slice(0, 10),
          stage_id: stageByName('Conclu') || defaultStage,
          workspace_id: defaultWorkspaceId,
        },
      ];

      await Promise.all(showcase.map((item) => axios.post(`${apiBaseUrl}/projects`, item)));
      loadData();
    } catch (error) {}
  };

  const openProject = (project) => {
    setSelectedProject(project);
    setProjectForm({
      title: project.title || '',
      description: project.description || '',
      type: project.type || 'incidente',
      demand_kind: project.demand_kind || 'corretiva',
      priority: project.priority || 'media',
      impact: project.impact || 'medio',
      urgency: project.urgency || 'media',
      status: project.status || 'aberto',
      owner: project.owner || '',
      customer_id: project.customer_id || '',
      company_id: project.company_id || '',
      workspace_id: project.workspace_id || '',
      start_date: project.start_date || '',
      due_date: project.due_date || '',
      sla_due: project.sla_due ? project.sla_due.slice(0, 16) : '',
    });
    setProjectModalOpen(true);
  };

  const saveProject = async () => {
    if (!projectForm.title.trim()) return;
    const inferredDemandKind = projectForm.demand_kind || inferDemandKind(projectForm.type);
    const payload = {
      ...projectForm,
      title: projectForm.title.trim(),
      demand_kind: inferredDemandKind,
      customer_id: projectForm.customer_id || null,
      company_id: projectForm.company_id || null,
      workspace_id: projectForm.workspace_id || null,
      sla_due: projectForm.sla_due || null,
    };
    try {
      if (selectedProject?.id) {
        await axios.put(`${apiBaseUrl}/projects/${selectedProject.id}`, {
          ...payload,
          stage_id: selectedProject.stage_id || null,
        });
      } else {
        await axios.post(`${apiBaseUrl}/projects`, payload);
      }
      closeProjectModal();
      loadData();
    } catch (error) {}
  };

  const handleProjectModalBackdrop = (event) => {
    if (event.target.classList.contains('kanban-modal-backdrop')) {
      closeProjectModal();
    }
  };

  const handleConfigBackdrop = (event, type) => {
    if (!event.target.classList.contains('kanban-config-backdrop')) return;
    if (type === 'workspace') {
      closeWorkspaceManager();
    } else {
      closeStageManager();
    }
  };

  const handleProjectField = (e) => {
    const { name, value } = e.target;
    setProjectForm((prev) => {
      if (name === 'type' && (!prev.demand_kind || prev.demand_kind === 'corretiva' || prev.demand_kind === 'evolutiva')) {
        const inferred = inferDemandKind(value);
        return { ...prev, [name]: value, demand_kind: inferred };
      }
      return { ...prev, [name]: value };
    });
  };

  const handleDrop = async (projectId, stageId) => {
    try {
      await axios.put(`${apiBaseUrl}/projects/${projectId}/stage`, { stage_id: stageId ?? null });
      setProjects((prev) => prev.map((p) => (p.id === projectId ? { ...p, stage_id: stageId ?? null } : p)));
    } catch (error) {}
  };

  const visibleProjects = useMemo(() => {
    return projects.filter((project) => {
      const matchesWorkspace = selectedWorkspaceId === 'all'
        ? true
        : String(project.workspace_id || '') === String(selectedWorkspaceId);
      const matchesDemandKind = demandKindFilter === 'all'
        ? true
        : (project.demand_kind || 'corretiva') === demandKindFilter;
      const matchesStatus = statusFilter === 'all'
        ? true
        : (project.status || 'aberto') === statusFilter;
      const queryText = `${project.code || ''} ${project.title || ''} ${project.owner || ''} ${project.customer_name || ''} ${project.company_name || ''}`.toLowerCase();
      const matchesQuery = !matrixQuery.trim() || queryText.includes(matrixQuery.trim().toLowerCase());
      return matchesWorkspace && matchesDemandKind && matchesStatus && matchesQuery;
    });
  }, [projects, selectedWorkspaceId, demandKindFilter, statusFilter, matrixQuery]);

  const grouped = useMemo(() => {
    const map = {};
    orderedStages.forEach((stage) => {
      map[stage.id] = [];
    });
    visibleProjects.forEach((project) => {
      const key = project.stage_id ?? 'unassigned';
      if (!map[key]) map[key] = [];
      map[key].push(project);
    });
    return map;
  }, [orderedStages, visibleProjects]);

  const columns = useMemo(() => {
    const unassigned = { id: null, name: 'Sem etapa', color: '#8F8F8F' };
    return [unassigned, ...orderedStages];
  }, [orderedStages]);

  const ganttData = useMemo(() => {
    const parsed = visibleProjects.map((project) => {
      const start = toDate(project.start_date) || toDate(project.created_at) || new Date();
      const finish = toDate(project.due_date) || toDate(project.completed_at) || toDate(project.sla_due) || new Date(start.getTime() + 7 * oneDay);
      const safeEnd = finish >= start ? finish : new Date(start.getTime() + oneDay);
      return { ...project, _start: startOfDay(start), _end: endOfDay(safeEnd) };
    });
    if (parsed.length === 0) {
      const now = new Date();
      return {
        items: [],
        minDate: startOfDay(now),
        maxDate: endOfDay(new Date(now.getTime() + 7 * oneDay)),
        totalDays: 7,
      };
    }
    const minDate = parsed.reduce((min, item) => (item._start < min ? item._start : min), parsed[0]._start);
    const maxDate = parsed.reduce((max, item) => (item._end > max ? item._end : max), parsed[0]._end);
    const totalDays = Math.max(1, Math.ceil((maxDate - minDate) / oneDay));
    return { items: parsed.sort((a, b) => a._start - b._start), minDate, maxDate, totalDays };
  }, [visibleProjects]);

  const roadmapGroups = useMemo(() => {
    const map = {};
    visibleProjects.forEach((project) => {
      const base = toDate(project.start_date) || toDate(project.due_date) || toDate(project.created_at) || new Date();
      const key = `${base.getFullYear()}-${String(base.getMonth() + 1).padStart(2, '0')}`;
      if (!map[key]) map[key] = [];
      map[key].push(project);
    });
    return Object.keys(map)
      .sort()
      .map((key) => ({ key, items: map[key].sort((a, b) => (a.priority || '').localeCompare(b.priority || '')) }));
  }, [visibleProjects]);

  const getMatrixSortValue = useCallback((project, field) => {
    if (!project) return null;
    switch (field) {
      case 'priority':
        return priorityWeight[(project.priority || '').toLowerCase()] || 0;
      case 'sli_percent':
      case 'slo_target_percent':
      case 'sla_target_hours':
      case 'sla_remaining_seconds':
        return Number(project[field] ?? 0);
      case 'status':
      case 'owner':
      case 'customer_name':
      case 'workspace_name':
      case 'demand_kind':
      case 'type':
      case 'code':
      case 'title':
        return String(project[field] || '').toLowerCase();
      default:
        return String(project[field] || '').toLowerCase();
    }
  }, []);

  const matrixProjects = useMemo(() => {
    let rows = [...visibleProjects];

    if (matrixPriorityFilter !== 'all') {
      rows = rows.filter((project) => (project.priority || '').toLowerCase() === matrixPriorityFilter);
    }

    if (matrixSlaFilter === 'within') {
      rows = rows.filter((project) => Boolean(project.sla_compliance));
    } else if (matrixSlaFilter === 'breached') {
      rows = rows.filter((project) => !project.sla_compliance);
    } else if (matrixSlaFilter === 'risk') {
      rows = rows.filter((project) => ['at_risk', 'breached'].includes((project.sli_status || '').toLowerCase()));
    }

    rows.sort((a, b) => {
      const aValue = getMatrixSortValue(a, matrixSortField);
      const bValue = getMatrixSortValue(b, matrixSortField);
      let result = 0;
      if (typeof aValue === 'number' && typeof bValue === 'number') {
        result = aValue - bValue;
      } else {
        result = String(aValue).localeCompare(String(bValue), 'pt-BR', { numeric: true, sensitivity: 'base' });
      }
      return matrixSortDirection === 'asc' ? result : -result;
    });

    return rows;
  }, [
    visibleProjects,
    matrixPriorityFilter,
    matrixSlaFilter,
    matrixSortField,
    matrixSortDirection,
    getMatrixSortValue,
  ]);

  const matrixSummary = useMemo(() => {
    const total = matrixProjects.length;
    let corretiva = 0;
    let evolutiva = 0;
    let withinSla = 0;
    let sumSli = 0;
    let sumSlo = 0;
    matrixProjects.forEach((project) => {
      if ((project.demand_kind || 'corretiva') === 'corretiva') corretiva += 1;
      if ((project.demand_kind || 'corretiva') === 'evolutiva') evolutiva += 1;
      if (project.sla_compliance) withinSla += 1;
      sumSli += Number(project.sli_percent || 0);
      sumSlo += Number(project.slo_target_percent || 0);
    });
    return {
      total,
      corretiva,
      evolutiva,
      withinSlaRate: total ? (withinSla / total) * 100 : 0,
      avgSli: total ? (sumSli / total) : 0,
      avgSlo: total ? (sumSlo / total) : 0,
    };
  }, [matrixProjects]);

  const handleMatrixSort = (field) => {
    setMatrixSortField((currentField) => {
      if (currentField === field) {
        setMatrixSortDirection((direction) => (direction === 'asc' ? 'desc' : 'asc'));
        return currentField;
      }
      const defaultDirection = ['priority', 'sli_percent', 'sla_target_hours', 'sla_remaining_seconds'].includes(field)
        ? 'desc'
        : 'asc';
      setMatrixSortDirection(defaultDirection);
      return field;
    });
  };

  const renderMatrixHead = (label, field) => {
    const active = matrixSortField === field;
    const arrow = active ? (matrixSortDirection === 'asc' ? '↑' : '↓') : '↕';
    return (
      <button
        type="button"
        className={`matrix-head-btn ${active ? 'active' : ''}`}
        onClick={() => handleMatrixSort(field)}
        title={`Ordenar por ${label}`}
      >
        <span>{label}</span>
        <small>{arrow}</small>
      </button>
    );
  };

  const renderKanbanView = () => (
    <div className="kanban-columns">
      {columns.map((stage) => (
        <div
          key={stage.id ?? 'unassigned'}
          className={`kanban-column ${dragOverStage === (stage.id ?? 'unassigned') ? 'drag-over' : ''}`}
          onDragEnter={() => setDragOverStage(stage.id ?? 'unassigned')}
        >
          <div className="kanban-column-header">
            <div>
              <span className="kanban-dot" style={{ background: stage.color || '#B10905' }} />
              <strong>{stage.name}</strong>
            </div>
            <span className="kanban-count">{(grouped[stage.id ?? 'unassigned'] || []).length}</span>
          </div>
          <div
            className="kanban-cards"
            onDragOver={(e) => {
              e.preventDefault();
              setDragOverStage(stage.id ?? 'unassigned');
            }}
            onDrop={(e) => {
              const projectId = Number(e.dataTransfer.getData('text/plain'));
              handleDrop(projectId, stage.id);
              setDragOverStage(null);
            }}
          >
            {(grouped[stage.id ?? 'unassigned'] || []).map((project) => (
              <div
                key={project.id}
                className={`kanban-card ${draggingId === project.id ? 'dragging' : ''}`}
                draggable
                onDragStart={(e) => {
                  setDraggingId(project.id);
                  e.dataTransfer.setData('text/plain', project.id);
                }}
                onDragEnd={() => {
                  setDraggingId(null);
                  setDragOverStage(null);
                }}
                onClick={() => openProject(project)}
              >
                <strong>{project.code} - {project.title}</strong>
                <div className="kanban-meta">
                  <span>{formatProjectTypeLabel(project.type)}</span>
                  <span>{project.priority || 'sem prioridade'}</span>
                </div>
                <small>{project.owner || 'Sem responsavel'}</small>
                <small>{project.customer_name || 'Sem cliente vinculado'}</small>
                <small>{project.workspace_name || 'Sem area de trabalho'}</small>
              </div>
            ))}
            {(grouped[stage.id ?? 'unassigned'] || []).length === 0 && (
              <div className="kanban-empty">Sem projetos</div>
            )}
          </div>
        </div>
      ))}
    </div>
  );

  const renderGanttView = () => (
    <div className="gantt-board">
      <div className="gantt-header">
        <strong>{formatDate(ganttData.minDate)} - {formatDate(ganttData.maxDate)}</strong>
        <span>{ganttData.items.length} projetos no periodo</span>
      </div>
      <div className="gantt-list">
        {ganttData.items.length === 0 && <div className="kanban-empty">Sem projetos para exibir.</div>}
        {ganttData.items.map((project) => {
          const offsetDays = Math.max(0, Math.floor((project._start - ganttData.minDate) / oneDay));
          const durationDays = Math.max(1, Math.ceil((project._end - project._start) / oneDay));
          const left = (offsetDays / ganttData.totalDays) * 100;
          const width = (durationDays / ganttData.totalDays) * 100;
          return (
            <div key={project.id} className="gantt-row" onClick={() => openProject(project)}>
              <div className="gantt-info">
                <strong>{project.code} - {project.title}</strong>
                <small>{project.owner || 'Sem responsavel'} - {project.status || 'aberto'} - {project.workspace_name || 'Sem area'}</small>
              </div>
              <div className="gantt-track">
                <div className="gantt-bar" style={{ left: `${left}%`, width: `${Math.max(width, 3)}%` }}>
                  <span>{formatDate(project._start)} - {formatDate(project._end)}</span>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );

  const renderRoadmapView = () => (
    <div className="roadmap-board">
      {roadmapGroups.length === 0 && <div className="kanban-empty">Sem projetos para roadmap.</div>}
      {roadmapGroups.map((group) => (
        <section key={group.key} className="roadmap-group">
          <header>
            <h4>{group.key}</h4>
            <span>{group.items.length} projetos</span>
          </header>
          <div className="roadmap-cards">
            {group.items.map((project) => (
              <article key={project.id} className="roadmap-card" onClick={() => openProject(project)}>
                <strong>{project.code}</strong>
                <p>{project.title}</p>
                <div className="kanban-meta">
                  <span>{formatProjectTypeLabel(project.type)}</span>
                  <span>{project.priority || 'sem prioridade'}</span>
                  <span>{project.status || 'aberto'}</span>
                </div>
                <small>{project.owner || 'Sem responsavel'} - {project.workspace_name || 'Sem area'} - Entrega {formatDate(project.due_date)}</small>
              </article>
            ))}
          </div>
        </section>
      ))}
    </div>
  );

  const renderMatrixView = () => (
    <div className="matrix-board">
      <div className="matrix-summary">
        <article>
          <strong>{matrixSummary.total}</strong>
          <span>Demandas</span>
        </article>
        <article>
          <strong>{matrixSummary.corretiva}</strong>
          <span>Corretivas</span>
        </article>
        <article>
          <strong>{matrixSummary.evolutiva}</strong>
          <span>Evolutivas</span>
        </article>
        <article>
          <strong>{matrixSummary.withinSlaRate.toFixed(1)}%</strong>
          <span>Dentro do SLA</span>
        </article>
        <article>
          <strong>{matrixSummary.avgSli.toFixed(1)}%</strong>
          <span>SLI Médio</span>
        </article>
        <article>
          <strong>{matrixSummary.avgSlo.toFixed(1)}%</strong>
          <span>SLO Médio</span>
        </article>
      </div>

      <div className="matrix-table-wrap">
        <div className="matrix-table">
          <div className="matrix-row matrix-head">
            {renderMatrixHead('Demanda', 'code')}
            {renderMatrixHead('Classificacao', 'demand_kind')}
            {renderMatrixHead('Prioridade', 'priority')}
            {renderMatrixHead('Status', 'status')}
            {renderMatrixHead('SLA', 'sla_remaining_seconds')}
            {renderMatrixHead('SLO', 'slo_target_percent')}
            {renderMatrixHead('SLI', 'sli_percent')}
            {renderMatrixHead('Responsavel', 'owner')}
            {renderMatrixHead('Cliente', 'customer_name')}
            {renderMatrixHead('Area', 'workspace_name')}
          </div>
          {matrixProjects.length === 0 && (
            <div className="matrix-empty">Sem demandas para os filtros selecionados.</div>
          )}
          {matrixProjects.map((project) => {
            const demandKind = project.demand_kind || 'corretiva';
            const slaDue = project.sla_due_effective || project.sla_due;
            const slaRemaining = formatDuration(project.sla_remaining_seconds, {
              invalidLabel: '-',
              zeroLabel: '0m',
              shortMinutes: true,
              minuteUnit: 'm',
            });
            return (
              <div
                key={project.id}
                className={`matrix-row ${project.sla_compliance ? '' : 'matrix-row--breached'}`}
                onClick={() => openProject(project)}
              >
                <span className="matrix-demand">
                  <strong>{project.code}</strong>
                  <small>{project.title}</small>
                </span>
                <span>
                  <span className={`matrix-badge demand-${demandKind}`}>
                    {formatDemandKindLabel(demandKind)}
                  </span>
                  <small>{formatProjectTypeLabel(project.type)}</small>
                </span>
                <span>{formatPriorityLabel(project.priority)}</span>
                <span>{formatStatusLabel(project.status)}</span>
                <span className="matrix-sla">
                  <strong>{project.sla_target_hours || '-'}h</strong>
                  <small>{formatDate(slaDue)} · {slaRemaining}</small>
                </span>
                <span>{Number(project.slo_target_percent || 0).toFixed(1)}%</span>
                <span>
                  <span className={`matrix-badge sli-${project.sli_status || 'on_track'}`}>
                    {Number(project.sli_percent || 0).toFixed(1)}%
                  </span>
                </span>
                <span>{project.owner || 'Sem responsável'}</span>
                <span>{project.customer_name || 'Sem cliente'}</span>
                <span>{project.workspace_name || 'Sem área'}</span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );

  return (
    <div className="kanban-board">
      <div className="kanban-header">
        <div>
          <h2>Projetos de Suporte</h2>
          <p>Gestao de projetos ITIL por etapas, com visoes Kanban, Matriz, Gantt e Roadmap.</p>
        </div>
        <div className="kanban-header-actions">
          <button className="kanban-toggle primary" onClick={() => openNewProject()}>
            Nova solicitacao
          </button>
          <button className="kanban-toggle showcase" onClick={createShowcaseProjects}>Carregar Showcase</button>
          <button className="kanban-toggle ghost" onClick={openWorkspaceManager}>
            Gestao das Areas
          </button>
          <button className="kanban-toggle ghost" onClick={openNewStage}>
            Gestao de Etapas
          </button>
        </div>
      </div>

      <div className="workspace-filter">
        <button
          className={selectedWorkspaceId === 'all' ? 'active' : ''}
          onClick={() => setSelectedWorkspaceId('all')}
        >
          Todas as Areas
        </button>
        {orderedWorkspaces.map((workspace) => (
          <button
            key={workspace.id}
            className={String(selectedWorkspaceId) === String(workspace.id) ? 'active' : ''}
            onClick={() => setSelectedWorkspaceId(String(workspace.id))}
          >
            <span className="kanban-dot" style={{ background: workspace.color || '#65202A' }} />
            {workspace.name}
          </button>
        ))}
      </div>

      <div className="matrix-filters">
        <div className="matrix-filters__kinds">
          <button className={demandKindFilter === 'all' ? 'active' : ''} onClick={() => setDemandKindFilter('all')}>
            Todas
          </button>
          <button className={demandKindFilter === 'corretiva' ? 'active' : ''} onClick={() => setDemandKindFilter('corretiva')}>
            Corretiva
          </button>
          <button className={demandKindFilter === 'evolutiva' ? 'active' : ''} onClick={() => setDemandKindFilter('evolutiva')}>
            Evolutiva
          </button>
        </div>
        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
          <option value="all">Todos os status</option>
          <option value="aberto">Aberto</option>
          <option value="planejamento">Planejamento</option>
          <option value="em_andamento">Em Andamento</option>
          <option value="em_validacao">Em Validação</option>
          <option value="concluido">Concluído</option>
          <option value="bloqueado">Bloqueado</option>
          <option value="cancelado">Cancelado</option>
        </select>
        <select value={matrixPriorityFilter} onChange={(e) => setMatrixPriorityFilter(e.target.value)}>
          <option value="all">Todas prioridades</option>
          <option value="critica">Critica</option>
          <option value="alta">Alta</option>
          <option value="media">Media</option>
          <option value="baixa">Baixa</option>
        </select>
        <select value={matrixSlaFilter} onChange={(e) => setMatrixSlaFilter(e.target.value)}>
          <option value="all">SLA e SLI: todos</option>
          <option value="within">Dentro do SLA</option>
          <option value="breached">Fora do SLA</option>
          <option value="risk">Em risco (SLI)</option>
        </select>
        <input
          type="text"
          placeholder="Buscar por código, título, responsável..."
          value={matrixQuery}
          onChange={(e) => setMatrixQuery(e.target.value)}
        />
      </div>

      <div className="kanban-view-switch">
        <button className={viewMode === 'kanban' ? 'active' : ''} onClick={() => setViewMode('kanban')}>Kanban</button>
        <button className={viewMode === 'matrix' ? 'active' : ''} onClick={() => setViewMode('matrix')}>Matriz</button>
        <button className={viewMode === 'gantt' ? 'active' : ''} onClick={() => setViewMode('gantt')}>Gantt</button>
        <button className={viewMode === 'roadmap' ? 'active' : ''} onClick={() => setViewMode('roadmap')}>Roadmap</button>
      </div>

      {showWorkspaceManager && (
        <div className="kanban-config-backdrop" onMouseDown={(event) => handleConfigBackdrop(event, 'workspace')}>
          <div className="kanban-config-modal" onMouseDown={(event) => event.stopPropagation()}>
            <div className="kanban-config-header">
              <div>
                <h3>Gestao das Areas de Trabalho</h3>
                <small>Centralize áreas, ordem e abertura rápida de cards.</small>
              </div>
              <button className="ghost" onClick={closeWorkspaceManager}>Fechar</button>
            </div>
            <div className="kanban-stage-manager">
              <div className="kanban-stage-form">
                <input
                  type="text"
                  placeholder="Nome da area de trabalho"
                  value={newWorkspace.name}
                  onChange={(e) => setNewWorkspace({ ...newWorkspace, name: e.target.value })}
                />
                <input
                  type="color"
                  value={newWorkspace.color}
                  onChange={(e) => setNewWorkspace({ ...newWorkspace, color: e.target.value })}
                />
                <button onClick={handleWorkspaceSave}>{editingWorkspace ? 'Atualizar Area' : 'Adicionar Area'}</button>
                {editingWorkspace && (
                  <button className="ghost" onClick={() => { setEditingWorkspace(null); setNewWorkspace({ name: '', color: '#65202A' }); }}>
                    Cancelar
                  </button>
                )}
              </div>
              <small className="kanban-manager-hint">Arraste as áreas para definir a ordem de exibição.</small>
              <div className="kanban-stage-list">
                {orderedWorkspaces.map((workspace) => (
                  <div
                    key={workspace.id}
                    className={`kanban-stage-item sortable ${dragWorkspaceId === workspace.id ? 'dragging' : ''} ${dragOverWorkspaceId === workspace.id ? 'drag-over' : ''}`}
                    draggable
                    onDragStart={() => setDragWorkspaceId(workspace.id)}
                    onDragOver={(e) => e.preventDefault()}
                    onDragEnter={() => setDragOverWorkspaceId(workspace.id)}
                    onDrop={() => {
                      if (dragWorkspaceId) {
                        handleWorkspaceReorder(dragWorkspaceId, workspace.id);
                      }
                      setDragWorkspaceId(null);
                      setDragOverWorkspaceId(null);
                    }}
                    onDragEnd={() => {
                      setDragWorkspaceId(null);
                      setDragOverWorkspaceId(null);
                    }}
                  >
                    <span className="kanban-drag-handle" aria-hidden="true">::</span>
                    <span className="kanban-dot" style={{ background: workspace.color || '#65202A' }} />
                    <strong>{workspace.name}</strong>
                    <small>Ordem {workspace.position ?? '-'} · {workspace.project_count || 0} projetos</small>
                    <div className="kanban-stage-actions">
                      <button className="ghost" onClick={() => openNewProject(workspace.id)}>Novo card</button>
                      <button className="ghost" onClick={() => handleWorkspaceEdit(workspace)}>Editar</button>
                      <button className="ghost danger" onClick={() => handleWorkspaceDelete(workspace.id)}>Excluir</button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {showStageManager && (
        <div className="kanban-config-backdrop" onMouseDown={(event) => handleConfigBackdrop(event, 'stage')}>
          <div className="kanban-config-modal" onMouseDown={(event) => event.stopPropagation()}>
            <div className="kanban-config-header">
              <div>
                <h3>Configuracao de Etapas</h3>
                <small>Crie, edite e reordene as etapas do fluxo de projetos.</small>
              </div>
              <button className="ghost" onClick={closeStageManager}>Fechar</button>
            </div>
            <div className="kanban-stage-manager">
              <div className="kanban-stage-form">
                <input
                  type="text"
                  placeholder="Nome da etapa"
                  value={newStage.name}
                  onChange={(e) => setNewStage({ ...newStage, name: e.target.value })}
                />
                <input
                  type="number"
                  placeholder="Ordem"
                  value={newStage.position}
                  onChange={(e) => setNewStage({ ...newStage, position: e.target.value })}
                />
                <input
                  type="color"
                  value={newStage.color}
                  onChange={(e) => setNewStage({ ...newStage, color: e.target.value })}
                />
                <button onClick={handleStageSave}>{editingStage ? 'Atualizar' : 'Adicionar'}</button>
                {editingStage && (
                  <button className="ghost" onClick={() => { setEditingStage(null); setNewStage({ name: '', color: '#B10905', position: '' }); }}>
                    Cancelar
                  </button>
                )}
              </div>
              <small className="kanban-manager-hint">Arraste as etapas para reordenar o Kanban.</small>
              <div className="kanban-stage-list">
                {orderedStages.map((stage) => (
                  <div
                    key={stage.id}
                    className={`kanban-stage-item sortable ${dragStageConfigId === stage.id ? 'dragging' : ''} ${dragOverStageConfigId === stage.id ? 'drag-over' : ''}`}
                    draggable
                    onDragStart={() => setDragStageConfigId(stage.id)}
                    onDragOver={(e) => e.preventDefault()}
                    onDragEnter={() => setDragOverStageConfigId(stage.id)}
                    onDrop={() => {
                      if (dragStageConfigId) {
                        handleStageReorder(dragStageConfigId, stage.id);
                      }
                      setDragStageConfigId(null);
                      setDragOverStageConfigId(null);
                    }}
                    onDragEnd={() => {
                      setDragStageConfigId(null);
                      setDragOverStageConfigId(null);
                    }}
                  >
                    <span className="kanban-drag-handle" aria-hidden="true">::</span>
                    <span className="kanban-dot" style={{ background: stage.color || '#B10905' }} />
                    <strong>{stage.name}</strong>
                    <small>Ordem {stage.position ?? '-'}</small>
                    <div className="kanban-stage-actions">
                      <button className="ghost" onClick={() => handleStageEdit(stage)}>Editar</button>
                      <button className="ghost danger" onClick={() => handleStageDelete(stage.id)}>Excluir</button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {viewMode === 'kanban' && renderKanbanView()}
      {viewMode === 'matrix' && renderMatrixView()}
      {viewMode === 'gantt' && renderGanttView()}
      {viewMode === 'roadmap' && renderRoadmapView()}

      {projectModalOpen && (
        <div className="kanban-modal-backdrop" onMouseDown={handleProjectModalBackdrop}>
          <div className="kanban-modal" onMouseDown={(event) => event.stopPropagation()}>
            <div className="kanban-drawer__header">
              <div>
                <h3>{selectedProject?.code || 'Nova solicitacao'}</h3>
                <p>{selectedProject?.title || 'Preencha os dados para abrir o card'}</p>
              </div>
              <button className="ghost" onClick={closeProjectModal}>Fechar</button>
            </div>
            <div className="kanban-drawer__form">
            <input name="title" placeholder="Titulo do projeto" value={projectForm.title} onChange={handleProjectField} />
            <textarea name="description" placeholder="Descricao" value={projectForm.description} onChange={handleProjectField} rows="3" />
            <select name="type" value={projectForm.type} onChange={handleProjectField}>
              {PROJECT_TYPE_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </select>
            <select name="demand_kind" value={projectForm.demand_kind} onChange={handleProjectField}>
              <option value="corretiva">Demanda Corretiva</option>
              <option value="evolutiva">Demanda Evolutiva</option>
            </select>
            <select name="priority" value={projectForm.priority} onChange={handleProjectField}>
              <option value="baixa">Prioridade Baixa</option>
              <option value="media">Prioridade Media</option>
              <option value="alta">Prioridade Alta</option>
              <option value="critica">Prioridade Critica</option>
            </select>
            <select name="impact" value={projectForm.impact} onChange={handleProjectField}>
              <option value="baixo">Impacto Baixo</option>
              <option value="medio">Impacto Medio</option>
              <option value="alto">Impacto Alto</option>
            </select>
            <select name="urgency" value={projectForm.urgency} onChange={handleProjectField}>
              <option value="baixa">Urgencia Baixa</option>
              <option value="media">Urgencia Media</option>
              <option value="alta">Urgencia Alta</option>
            </select>
            <select name="status" value={projectForm.status} onChange={handleProjectField}>
              <option value="aberto">Aberto</option>
              <option value="planejamento">Planejamento</option>
              <option value="em_andamento">Em Andamento</option>
              <option value="em_validacao">Em Validacao</option>
              <option value="concluido">Concluido</option>
              <option value="bloqueado">Bloqueado</option>
              <option value="cancelado">Cancelado</option>
            </select>
            <input name="owner" placeholder="Responsavel" value={projectForm.owner} onChange={handleProjectField} />
            <select name="customer_id" value={projectForm.customer_id} onChange={handleProjectField}>
              <option value="">Cliente (opcional)</option>
              {customers.map((customer) => (
                <option key={customer.id} value={customer.id}>{customer.name}</option>
              ))}
            </select>
            <select name="company_id" value={projectForm.company_id} onChange={handleProjectField}>
              <option value="">Empresa (opcional)</option>
              {companies.map((company) => (
                <option key={company.id} value={company.id}>{company.name}</option>
              ))}
            </select>
            <select name="workspace_id" value={projectForm.workspace_id} onChange={handleProjectField}>
              <option value="">Area de trabalho</option>
              {orderedWorkspaces.map((workspace) => (
                <option key={workspace.id} value={workspace.id}>{workspace.name}</option>
              ))}
            </select>
            <label>
              Inicio
              <input type="date" name="start_date" value={projectForm.start_date} onChange={handleProjectField} />
            </label>
            <label>
              Previsao de entrega
              <input type="date" name="due_date" value={projectForm.due_date} onChange={handleProjectField} />
            </label>
            <label>
              SLA limite (opcional)
              <input type="datetime-local" name="sla_due" value={projectForm.sla_due} onChange={handleProjectField} />
            </label>
            <div className="project-policy-preview">
              <h4>Politica automatica (ITIL)</h4>
              <div className="project-policy-preview__grid">
                <div>
                  <small>Classificacao</small>
                  <strong>{formatDemandKindLabel(formPolicyPreview.demandKind)}</strong>
                </div>
                <div>
                  <small>SLA alvo</small>
                  <strong>{formPolicyPreview.slaHours}h</strong>
                </div>
                <div>
                  <small>SLO alvo</small>
                  <strong>{formPolicyPreview.sloTarget}%</strong>
                </div>
                <div>
                  <small>SLI</small>
                  <strong>Calculado automatico</strong>
                </div>
              </div>
            </div>
              <button onClick={saveProject}>{selectedProject?.id ? 'Salvar solicitacao' : 'Criar solicitacao'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default KanbanBoard;
